import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Lock } from "lucide-react";
import Emblem from "./Emblem";
import Admin from "./Admin";
import LanguageSwitcher from "@/i18n/LanguageSwitcher";
import { useLang } from "@/i18n/LangContext";
import { scrollToSection } from "@/lib/scroll";

const Navbar = () => {
  const { t } = useLang();
  const LINKS = [
    { label: t("nav.books"), href: "#libros" },
    { label: t("nav.manifesto"), href: "#manifiesto" },
    { label: t("nav.blog"), href: "#bitacora" },
    { label: t("nav.channels"), href: "#contacto" },
  ];
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#0D0D0E]/85 backdrop-blur-xl border-b border-[#F4F1EA]/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-[70px] flex items-center justify-between">
        <button
          data-testid="nav-brand"
          onClick={() => go("#inicio")}
          className="flex items-center gap-3 group"
        >
          <Emblem className="w-11 h-auto transition-transform duration-500 group-hover:scale-110" animate={false} />
          <span className="font-display text-lg tracking-wide text-[#F4F1EA]">
            Javier <span className="text-[#D4AF37] italic">Alagrande</span>
          </span>
        </button>

        <div className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => go(l.href)}
              className="text-xs tracking-[0.2em] uppercase text-[#A0A0A5] hover:text-[#D4AF37] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta-contacto"
            onClick={() => go("#contacto")}
            className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase px-5 py-2.5 gold-hairline text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors duration-300"
          >
            <Lock className="w-3.5 h-3.5" />
            {t("nav.cta")}
          </button>
        </div>

        <LanguageSwitcher />
        <Admin />

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-[#F4F1EA] p-2"
          onClick={() => setOpen(!open)}
          aria-label="Menú"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#0D0D0E]/95 backdrop-blur-xl border-b border-[#F4F1EA]/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => go(l.href)}
                  className="text-left text-sm tracking-[0.2em] uppercase text-[#A0A0A5] hover:text-[#D4AF37] transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-cta-contacto"
                onClick={() => go("#contacto")}
                className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase px-5 py-3 gold-hairline text-[#D4AF37] w-fit"
              >
                <Lock className="w-3.5 h-3.5" />
                {t("nav.cta")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
